//@flow
import React, { createContext, useState, useContext, useEffect } from 'react';

type ThemeMode = 'light' | 'dark';

type ThemeContextType = {
    mode: ThemeMode,
    toggleMode: () => void,
    setMode: (mode: ThemeMode) => void,
};

const ThemeContext = createContext <? ThemeContextType > (null);

export const useThemeMode = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useThemeMode must be used within ThemeModeProvider');
    }
    return context;
};

type Props = {
    children: React$Node,
};

export const ThemeModeProvider = ({ children }: Props): React$Node => {
    const [mode, setModeState] = useState < ThemeMode > (() => {
        // Check localStorage first
        const saved = localStorage.getItem('themeMode');
        if (saved === 'light' || saved === 'dark') {
            return saved;
        }

        // Check system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        return prefersDark ? 'dark' : 'light';
    });

    // Persist mode to localStorage
    useEffect(() => {
        localStorage.setItem('themeMode', mode);
        // Update body data attribute for CSS
        if (document.body) {
            document.body.setAttribute('data-theme', mode);
        }
    }, [mode]);

    // Listen for system preference changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
            // Only auto-switch if user hasn't manually set a preference
            if (!localStorage.getItem('themeMode')) {
                setModeState(e.matches ? 'dark' : 'light');
            }
        };

        // Modern browsers
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        }
        // Legacy browsers
        else if (mediaQuery.addListener) {
            mediaQuery.addListener(handleChange);
            return () => mediaQuery.removeListener(handleChange);
        }
    }, []);

    const toggleMode = () => {
        setModeState(prev => prev === 'light' ? 'dark' : 'light');
    };

    const setMode = (newMode: ThemeMode) => {
        setModeState(newMode);
    };

    const value = {
        mode,
        toggleMode,
        setMode,
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};
